import { Pipe, PipeTransform } from '@angular/core';

import { TranslationService } from './translation.service';

/**
 * `{{ 'SomeKey' | t }}` / `{{ 'SomeKey' | t: { count: 3 } }}`
 *
 * IMPURE, WITH A MEMO — and the memo is what makes that affordable.
 *
 * The first version of this pipe was PURE, on the reasoning that an impure pipe
 * re-runs on every change-detection pass and this app has a recorded performance
 * incident from per-cell work during change detection. That reasoning was wrong,
 * and a test caught it: a pure pipe is only re-invoked when its ARGUMENTS change,
 * and the argument here is a literal key that never changes. Marking the view for
 * check does not invalidate a pure pipe's cached result. So switching language
 * left every already-rendered string frozen at the previous language — the exact
 * feature the pipe exists to provide, silently broken, on a screen that looked
 * fine until you changed language twice.
 *
 * So it is impure, but the work per call is a version comparison and — only when
 * the dictionary has actually changed — one map lookup. It also returns the SAME
 * STRING REFERENCE while nothing has changed, so bindings downstream do not see a
 * new value and nothing re-renders. That is nothing like the full-array scans
 * behind the incident the earlier comment was invoking.
 */
@Pipe({ name: 't', pure: false })
export class TranslatePipe implements PipeTransform {
  private lastKey: string | null = null;
  private lastParamsJson = '';
  private lastVersion = -1;
  private lastResult = '';

  constructor(private translation: TranslationService) {}

  transform(key: string | null | undefined, params?: Record<string, string | number>): string {
    if (!key) return '';
    // Params are compared by value, not identity: a template writing
    // `| t: { visible: 6 }` builds a NEW object every pass, so an identity check
    // would miss the cache on every call and defeat the whole memo.
    const paramsJson = params ? JSON.stringify(params) : '';
    const version = this.translation.version$.value;

    if (key === this.lastKey && paramsJson === this.lastParamsJson && version === this.lastVersion) {
      return this.lastResult;
    }

    this.lastKey = key;
    this.lastParamsJson = paramsJson;
    this.lastVersion = version;
    this.lastResult = this.translation.t(key, params);
    return this.lastResult;
  }
}
